import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/pro-duotone-svg-icons/faXmark';
import { firstValueFrom } from 'rxjs';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';

import { SettingsShell } from '../settings-shell/settings-shell';
import { ThemeEditor } from '../settings-theme/theme-editor/theme-editor';
import { RegionSelect } from '../region-select/region-select';
import { AppConfigStore } from '../../../../state/app-config.repository';
import { RoomsStore } from '../../../../state/rooms.repository';
import { TextInput } from '../../../../shared/components/form-fields/text-input/text-input';
import { Button } from '../../../../shared/components/ui/buttons/gradient-btn/button';
import { IconBtn } from '../../../../shared/components/ui/buttons/icon-btn/icon-btn';
import {
  ConfirmDialog,
  ConfirmDialogData,
} from '../../../../shared/components/ui/confirm-dialog/confirm-dialog';
import { AppConfigService } from '../../../../core/services/app-config.service';

/** How long a failed-save message stays visible before it dismisses itself. */
export const SAVE_ERROR_AUTO_DISMISS_MS = 5000;

const notBlank = (control: AbstractControl<string>): ValidationErrors | null =>
  control.value.trim() ? null : { blank: true };

/**
 * App-wide settings for users with `editAppSettings`: the instance name and
 * server region, and the theme. Everything edits the AppConfigStore draft and
 * is saved together.
 */
@Component({
  selector: 'andor-app-settings',
  standalone: true,
  imports: [
    TranslatePipe,
    SettingsShell,
    ThemeEditor,
    RegionSelect,
    TextInput,
    Button,
    IconBtn,
    FaIconComponent,
  ],
  templateUrl: './settings-app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './settings-app.scss',
})
export class SettingsApp {
  protected readonly appConfigStore = inject(AppConfigStore);
  private readonly appConfigService = inject(AppConfigService);
  private readonly roomsStore = inject(RoomsStore);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);

  protected readonly faXmark = faXmark;
  protected readonly saving = signal(false);
  /** Server message of the last failed save; auto-dismissed, or cleared on Save/Cancel/X. */
  protected readonly error = signal<string | null>(null);
  #errorTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly config = toSignal(this.appConfigStore.appSettings$, {
    initialValue: this.appConfigStore.value,
  });
  protected readonly gateway = computed(() => this.config().gateway);

  readonly appTitle = new FormControl(this.appConfigStore.value.title, {
    nonNullable: true,
    validators: [notBlank, Validators.maxLength(64)],
  });
  private readonly _appTitleEvents = toSignal(this.appTitle.events);
  /** Translation key of the name's validation error, if any. */
  readonly titleError = computed(() => {
    this._appTitleEvents();
    if (this.appTitle.hasError('blank')) return _('Enter a name for your app.');
    if (this.appTitle.hasError('maxlength')) {
      return _('This field must not have more than 64 chars.');
    }
    return null;
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.#errorTimer));
    // The store keeps the raw text so typing spaces works; it is trimmed on save.
    this.appTitle.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((title) => this.appConfigStore.update({ title }));
    // Cancel and realtime updates replace the draft title underneath the field.
    this.appConfigStore.appTitle$.pipe(takeUntilDestroyed()).subscribe((title) => {
      if (title !== this.appTitle.value) this.appTitle.setValue(title);
    });
  }

  selectRegion(host: string | null) {
    if (host) this.appConfigStore.update({ gateway: host });
  }

  async save() {
    if (this.saving() || this.titleError()) return;
    if (!(await this.confirmRegionChange())) return;
    this.saving.set(true);
    this.dismissError();
    try {
      const result = await this.appConfigService.syncConfig();
      if ('error' in result) {
        // Keep the draft (and pending files) so the user can fix and retry.
        this.showError(result.message);
        return;
      }
      this.snackBar.open(
        this.translateService.instant('App Settings saved successfully!'),
        undefined,
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        }
      );
    } finally {
      this.saving.set(false);
    }
  }

  cancel() {
    this.dismissError();
    this.appConfigStore.revert();
  }

  dismissError() {
    clearTimeout(this.#errorTimer);
    this.#errorTimer = undefined;
    this.error.set(null);
  }

  private showError(message: string) {
    clearTimeout(this.#errorTimer);
    this.error.set(message);
    this.#errorTimer = setTimeout(() => this.dismissError(), SAVE_ERROR_AUTO_DISMISS_MS);
  }

  /**
   * People already in a call keep their current gateway until they rejoin, so
   * a region change while rooms are occupied briefly splits those rooms.
   */
  private async confirmRegionChange(): Promise<boolean> {
    if (this.appConfigStore.value.gateway === this.appConfigStore.saved().gateway) return true;
    const count = this.roomsStore.rooms.reduce((sum, room) => sum + room.memberCount, 0);
    if (!count) return true;
    const data: ConfirmDialogData = {
      title: this.translateService.instant('Change server region?'),
      message: this.translateService.instant(
        'People in rooms right now: [count]. They stay on the current region until they rejoin, while new joins use the new one.',
        { count }
      ),
      confirmLabel: this.translateService.instant('Change region'),
    };
    const ref = this.dialog.open<ConfirmDialog, ConfirmDialogData, boolean>(ConfirmDialog, {
      panelClass: 'confirm-dialog',
      backdropClass: 'confirm-backdrop',
      data,
    });
    return (await firstValueFrom(ref.afterClosed())) === true;
  }
}
